import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";

/**
 * Typed, validated client environment for the mobile app (Vite).
 *
 * Every var is optional: the app runs fully against same-origin proxies
 * with zero env (CI's `vite build` has none), and Sentry / Dynamsoft simply
 * stay disabled when their vars are absent. Validation is about catching
 * MALFORMED values (e.g. a non-URL VITE_API_URL) loudly:
 *  - at build/dev start: vite.config.mts calls `parseMobileEnv` with the
 *    merged `.env` + shell env, so a bad value fails before the bundle exists;
 *  - at app boot: this module parses the baked `import.meta.env` on first
 *    import, so a bad value baked into a bundle fails immediately on load.
 *
 * Vite builtins (`import.meta.env.DEV`, `MODE`, ...) are not part of the
 * schema; keep reading those from `import.meta.env` directly. Build-time
 * orchestration vars (MOBILE_PORT, BUILD_CHANNEL, SENTRY_AUTH_TOKEN, ...)
 * are tooling config, not app env; they stay in vite.config.mts.
 */

const client = {
  /** Dev-server proxy targets, written per-worktree by scripts/worktree-setup.sh. */
  VITE_API_PROXY_TARGET: z.url().optional(),
  /** Staff API origin baked into native/OTA bundles; unset = same-origin proxy. */
  VITE_API_URL: z.url().optional(),
  /**
   * The web app's own public origin (my.<env>.vidimo.app). A connect page,
   * served from the connect origin, sends the host back here; unset (local
   * dev, native bundles) means the page is already on it.
   */
  VITE_APP_URL: z.url().optional(),
  /**
   * Whether the API this bundle talks to holds each social provider's
   * credentials. Build-time, not a runtime probe: the answer changes only with
   * a deployment, and a first paint that has to wait for a fetch cannot be
   * prerendered. Unset means off — see src/auth/auth-features.ts.
   */
  VITE_APPLE_SIGN_IN_ENABLED: z.stringbool().optional(),
  /**
   * The connect pages' origin (connect.<env>.vidimo.app): the same bundle,
   * served where Meta's SDK shares no storage with the signed-in app. With
   * VITE_APP_URL, it keeps each page on its own origin (connect/origins.ts).
   */
  VITE_CONNECT_URL: z.url().optional(),
  VITE_DYNAMSOFT_MRZ_LICENSE: z.string().optional(),
  VITE_GOOGLE_MAPS_API_KEY: z.string().optional(),
  /** Google's half of the pair documented on VITE_APPLE_SIGN_IN_ENABLED. */
  VITE_GOOGLE_SIGN_IN_ENABLED: z.stringbool().optional(),
  VITE_POWERSYNC_PROXY_TARGET: z.url().optional(),
  VITE_SENTRY_DSN: z.url().optional(),
  VITE_SENTRY_ENVIRONMENT: z.string().optional(),
} as const;

type RuntimeEnv = Record<string, string | boolean | number | undefined>;

export function parseMobileEnv(runtimeEnv: RuntimeEnv) {
  return createEnv({
    client,
    clientPrefix: "VITE_",
    emptyStringAsUndefined: true,
    runtimeEnv,
    server: {},
    skipValidation: !!runtimeEnv.SKIP_ENV_VALIDATION,
  });
}

// In the browser (and vitest) `import.meta.env` exists; when this module is
// pulled into a plain Node context (vite.config.mts imports it before Vite
// defines import.meta.env) fall back to process.env.
const nodeProcessEnv =
  (globalThis as { process?: { env?: Record<string, string | undefined> } })
    .process?.env ?? {};

export const env = parseMobileEnv(
  typeof import.meta.env === "undefined" ? nodeProcessEnv : import.meta.env
);

/** Schema key list, asserted against turbo.json by test/turbo-env.test.ts. */
export const envClientKeys: readonly string[] = Object.keys(client);
